import { Link } from "react-router-dom";
import { TarjetaGrafico } from "./ComunesDashboard";

export interface ObjetivoConProgreso {
  objetivo: number;
  actual: number;
  moneda?: string;
}

// Exportado para que la Vista General del Dashboard (DashboardGeneralPage)
// pueda tipar la respuesta de /dashboard/ejecutivo y reusar este mismo
// componente sin redeclarar la forma en dos archivos distintos.
export interface DatosObjetivos {
  definidos: boolean;
  honorariosCobrados: ObjetivoConProgreso | null;
  escritos: ObjetivoConProgreso | null;
  audiencias: ObjetivoConProgreso | null;
  nuevosClientes: ObjetivoConProgreso | null;
}

// Exportado junto con `listaObjetivos` para que la Vista General del
// Dashboard pueda mostrar los mismos objetivos en un layout más compacto
// (grilla horizontal en vez de barras apiladas) sin reimplementar el
// cálculo de porcentaje/alcanzado — ver DashboardGeneralPage.tsx.
export function BarraObjetivo({
  etiqueta,
  actual,
  objetivo,
  moneda,
}: {
  etiqueta: string;
  actual: number;
  objetivo: number;
  moneda?: string;
}) {
  const porcentaje = objetivo > 0 ? Math.min((actual / objetivo) * 100, 100) : 0;
  const alcanzado = actual >= objetivo;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3 text-sm">
        <span className="min-w-0 flex-1 truncate font-medium text-texto">{etiqueta}</span>
        <span className="flex-shrink-0 whitespace-nowrap font-medium text-texto-secundario">
          {moneda ? `${moneda} ` : ""}
          {actual.toLocaleString("es-AR")} / {objetivo.toLocaleString("es-AR")}
        </span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-borde">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${alcanzado ? "bg-exito" : "bg-secundario"}`}
          style={{ width: `${Math.max(porcentaje, 3)}%` }}
        />
      </div>
    </div>
  );
}

export interface ObjetivoConEtiqueta extends ObjetivoConProgreso {
  etiqueta: string;
}

// Único punto de verdad para "qué objetivos están definidos" — cada
// null se omite (nunca una barra al 0%). Reutilizado tal cual por
// PanelObjetivos (detalle, Financiero) y por la Vista General (resumen
// compacto), para que ambas muestren exactamente los mismos objetivos.
export function listaObjetivos(objetivos: DatosObjetivos): ObjetivoConEtiqueta[] {
  return [
    objetivos.honorariosCobrados && { etiqueta: "Honorarios cobrados", ...objetivos.honorariosCobrados },
    objetivos.escritos && { etiqueta: "Escritos realizados", ...objetivos.escritos },
    objetivos.audiencias && { etiqueta: "Audiencias", ...objetivos.audiencias },
    objetivos.nuevosClientes && { etiqueta: "Nuevos clientes", ...objetivos.nuevosClientes },
  ].filter((i): i is ObjetivoConEtiqueta => Boolean(i));
}

// Objetivos configurables manualmente (una fila por tenant, no historizada
// por mes — ver migrations/0009_dashboard_ejecutivo.sql). Cada objetivo en
// null se omite (no una barra al 0%); si ninguno está definido, se invita a
// configurarlos.
export function PanelObjetivos({ objetivos }: { objetivos: DatosObjetivos }) {
  const items = listaObjetivos(objetivos);

  return (
    <TarjetaGrafico titulo="Objetivos mensuales">
      {items.length === 0 ? (
        <p className="text-sm text-texto-secundario">
          Sin objetivos definidos.{" "}
          <Link to="/configuracion" className="font-medium text-secundario hover:underline">
            Configurarlos
          </Link>
          .
        </p>
      ) : (
        <div className="space-y-5">
          {items.map((i) => (
            <BarraObjetivo key={i.etiqueta} etiqueta={i.etiqueta} actual={i.actual} objetivo={i.objetivo} moneda={i.moneda} />
          ))}
        </div>
      )}
    </TarjetaGrafico>
  );
}
